<template>
  <div class="web-order-line-items">
    <div class="header">
      <h1>Web Order Line Items</h1>
    </div>

    <div v-if="isLoading" class="loading">Loading line items...</div>
    <div v-else-if="error" class="error">{{ error }}</div>
    <div v-else class="table-container">
      <table class="data-table">
        <thead>
          <tr>
            <th>Customer Part</th>
            <th>Hose</th>
            <th>Length</th>
            <th>Cut Length</th>
            <th>Fitting A</th>
            <th>Fitting B</th>
            <th>Orientation</th>
            <th>Quantity</th>
            <th>Cost</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in lineItems" :key="item.LineID">
            <td>{{ item.CustPart }}</td>
            <td>{{ item.Hose }}</td>
            <td>{{ item.Length }}</td>
            <td>{{ item.CutLength }}</td>
            <td>{{ item.FittingA }}</td>
            <td>{{ item.FittingB }}</td>
            <td>{{ item.Orientation }}</td>
            <td>{{ item.Quantity }}</td>
            <td>
              <span v-if="item.NetPrice !== null && item.NetPrice !== undefined" class="cost">€{{ Number(item.NetPrice).toFixed(2) }}</span>
              <span v-else class="no-price">No price set</span>
            </td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <td colspan="8" class="total-label">Total Cost:</td>
            <td class="total-cost">€{{ Number(totalCost).toFixed(2) }}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'WebOrderLineItemsView',
  data() {
    return {
      lineItems: [],
      isLoading: true,
      error: null,
      orderId: null
    };
  },
  computed: {
    totalCost() {
      return this.lineItems.reduce((sum, item) => {
        return sum + (item.NetPrice || 0);
      }, 0);
    }
  },
  methods: {
    async fetchLineItems() {
      if (!this.orderId) {
        this.error = 'No order ID provided';
        this.isLoading = false;
        return;
      }
      
      this.isLoading = true;
      try {
        const response = await axios.get(`/weborderlineitems/${this.orderId}`);
        this.lineItems = response.data || [];
        // Don't recalculate costs for placed orders - use stored NetPrice
      } catch (error) {
        console.error('Error fetching line items:', error);
        this.error = error.response?.data?.message || 'Failed to fetch line items';
        this.lineItems = [];
      } finally {
        this.isLoading = false;
      }
    },
    async updateLineItemStatus(lineItemId, newStatus) {
      try {
        await axios.put(`/weborderlineitems/${lineItemId}/status`, {
          status: newStatus
        });
        await this.fetchLineItems();
      } catch (error) {
        console.error('Error updating line item status:', error);
        this.error = error.response?.data?.message || 'Failed to update line item status';
      }
    },
    async deleteLineItem(lineItemId) {
      if (!confirm('Are you sure you want to delete this line item?')) {
        return;
      }
      try {
        await axios.delete(`/weborderlineitems/${lineItemId}`);
        await this.fetchLineItems();
      } catch (error) {
        console.error('Error deleting line item:', error);
        this.error = error.response?.data?.message || 'Failed to delete line item';
      }
    }
  },
  watch: {
    '$route.params': {
      immediate: true,
      handler(params) {
        this.orderId = params.id ? parseInt(params.id) : null;
        if (this.orderId) {
          this.fetchLineItems();
        }
      }
    }
  },
  mounted() {
    // orderId will be set by the route watcher
  }
};
</script>

<style scoped>
.web-order-line-items {
  padding: 2rem;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
}

h1 {
  color: var(--text-primary);
  font-size: 1.8rem;
  font-weight: 600;
}

.table-container {
  background: var(--background);
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
}

.data-table th,
.data-table td {
  padding: 1rem;
  text-align: left;
  border-bottom: 1px solid #e5e7eb;
}

.data-table th {
  background-color: var(--background-alt);
  font-weight: 600;
  color: var(--text-primary);
  font-size: 0.875rem;
}

.data-table td {
  font-size: 0.875rem;
}

.cost {
  font-weight: 500;
  color: var(--text-primary);
}

.no-price {
  color: var(--text-secondary);
  font-style: italic;
}

.total-label {
  text-align: right;
  font-weight: 600;
  color: var(--text-primary);
}

.total-cost {
  font-weight: 600;
  color: var(--primary-color);
}

.loading {
  text-align: center;
  padding: 2rem;
  color: var(--text-secondary);
}

.error {
  background-color: #fee2e2;
  color: #991b1b;
  padding: 1rem;
  border-radius: 0.5rem;
  margin: 1rem 0;
  text-align: center;
  border: 1px solid #fecaca;
}

@media (max-width: 768px) {
  .web-order-line-items {
    padding: 1rem;
  }

  .header {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }

  .data-table {
    display: block;
    overflow-x: auto;
    white-space: nowrap;
  }
}
</style> 